import { AUTH_EVENT } from 'constants/events';
import {
  LOCAL_STORAGE,
  NATIVE_TOKEN_REQUEST,
  PLATFORMS,
  USER_ROLES
} from 'constants/general';
import { ROUTES } from 'constants/routes';
import { DecodedTokenDataType } from 'interfaces/subscription.interface';
import jwtDecode from 'jwt-decode';
import { postAccessTokenRequestToNative } from 'bridge/platform-messaging.bridge';
import { validateAuth0Token } from 'services/auth.service';
import { captureException } from 'services/posthog.service';
import { updateIsTokenAddedToInterceptor } from 'store/reducer/setting/slice';
import { setIsUserAdmin } from 'store/reducer/user/slice';
import { getAppDispatch, getAppState } from 'store/store-access';
import { isAccessTokenValid } from 'utils/auth-token-validation.util';
import {
  getLocalStorage,
  removeLocalStorage,
  updateLocalStorage
} from 'utils/helpers/storage';
import { isRouteEmbedded } from 'utils/routes.util';
import { v4 as uuidv4 } from 'uuid';
import {
  ACCESS_TOKEN_REQUEST_EVENT,
  APPLY_NATIVE_ACCESS_TOKEN_SOURCE,
  NATIVE_TOKEN_REQUEST_OUTCOME,
  NATIVE_TOKEN_REQUEST_REASON
} from 'enums';
import {
  ApplyNativeAccessTokenOptions,
  EmbeddedAuthFailedDetail,
  PendingResolver
} from 'interfaces/bridge.interface';

let isRequestingToken = false;
let pendingRequestId: string | null = null;
let retryCount = 0;
let requestTimeoutId: ReturnType<typeof setTimeout> | null = null;
let lastRequestAt = 0;
let lastRequestReason: NATIVE_TOKEN_REQUEST_REASON =
  NATIVE_TOKEN_REQUEST_REASON.UNAUTHORIZED;
let pendingResolvers: PendingResolver[] = [];
let hasReceivedNativeTokenPush = false;

const clearRequestTimeout = (): void => {
  if (requestTimeoutId !== null) {
    clearTimeout(requestTimeoutId);
    requestTimeoutId = null;
  }
};

const rejectPendingResolvers = (error: Error): void => {
  pendingResolvers.forEach(({ reject }) => reject(error));
  pendingResolvers = [];
};

const resolvePendingResolvers = (token: string): void => {
  pendingResolvers.forEach(({ resolve }) => resolve(token));
  pendingResolvers = [];
};

const detectNativeDesktopPlatform = (): string | null => {
  const storedPlatform = getAppState()?.setting?.platform;
  if (storedPlatform === PLATFORMS.MAC || storedPlatform === PLATFORMS.WIN) {
    return storedPlatform;
  }

  if (window.webkit?.messageHandlers) {
    return PLATFORMS.MAC;
  }

  if (window.chrome?.webview) {
    return PLATFORMS.WIN;
  }

  return null;
};

export const isEmbeddedNativeContext = (): boolean => {
  if (!isRouteEmbedded) {
    return false;
  }

  return detectNativeDesktopPlatform() !== null;
};

export const isNativeTokenRequestInProgress = (): boolean => isRequestingToken;

const markNativeHostTokenPushReceived = (
  source: APPLY_NATIVE_ACCESS_TOKEN_SOURCE
): void => {
  if (source === APPLY_NATIVE_ACCESS_TOKEN_SOURCE.INIT_APP_META_DATA) {
    hasReceivedNativeTokenPush = true;
  }
};

const isTokenExpired = (token: string): boolean => !isAccessTokenValid(token);

const dispatchTokenSuccess = (accessToken: string): void => {
  const tokenInfo: DecodedTokenDataType = jwtDecode(accessToken);

  updateLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN, accessToken);
  getAppDispatch()?.(
    setIsUserAdmin(
      tokenInfo?.['https://api.focusbear.io/roles']?.includes(
        USER_ROLES.ADMIN
      ) ?? false
    )
  );
  getAppDispatch()?.(updateIsTokenAddedToInterceptor(true));
  window.dispatchEvent(new Event(AUTH_EVENT.UPDATED, { composed: true }));
};

const dispatchNativeTokenRequestStarted = (): void => {
  window.dispatchEvent(
    new Event(AUTH_EVENT.TOKEN_REQUEST_STARTED, { composed: true })
  );
};

const dispatchNativeTokenRequestEnded = (): void => {
  window.dispatchEvent(
    new Event(AUTH_EVENT.TOKEN_REQUEST_ENDED, { composed: true })
  );
};

const dispatchEmbeddedNetworkUnavailable = (): void => {
  window.dispatchEvent(
    new Event(AUTH_EVENT.EMBEDDED_NETWORK_UNAVAILABLE, { composed: true })
  );
};

const isBrowserOffline = (): boolean =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

const hasValidStoredAccessToken = (): boolean => {
  const currentToken = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);
  return !!currentToken && isAccessTokenValid(currentToken);
};

const handleRecoverableNativeTokenFailure = (
  outcome: NATIVE_TOKEN_REQUEST_OUTCOME
): void => {
  if (hasValidStoredAccessToken()) {
    dispatchEmbeddedNetworkUnavailable();
    return;
  }

  redirectEmbeddedToErrorPage(outcome);
};

export const redirectEmbeddedToErrorPage = (
  outcome: NATIVE_TOKEN_REQUEST_OUTCOME = NATIVE_TOKEN_REQUEST_OUTCOME.MAX_RETRIES
): void => {
  removeLocalStorage([
    LOCAL_STORAGE.AUTH_BEARER_TOKEN,
    LOCAL_STORAGE.AUTH_BEARER_REFRESH,
    LOCAL_STORAGE.AUTH_ID
  ]);

  if (isRouteEmbedded) {
    window.dispatchEvent(
      new CustomEvent<EmbeddedAuthFailedDetail>(
        AUTH_EVENT.EMBEDDED_AUTH_FAILED,
        {
          detail: { outcome },
          composed: true
        }
      )
    );
    return;
  }

  window.location.pathname = ROUTES.ERROR;
};

const handleTokenRequestFailure = (
  outcome: NATIVE_TOKEN_REQUEST_OUTCOME
): void => {
  isRequestingToken = false;
  retryCount = NATIVE_TOKEN_REQUEST.MIN_RETRIES;
  clearRequestTimeout();
  dispatchNativeTokenRequestEnded();
  rejectPendingResolvers(new Error(`Native token request ${outcome}`));
  handleRecoverableNativeTokenFailure(outcome);
};

const scheduleRequestTimeout = (): void => {
  clearRequestTimeout();
  requestTimeoutId = setTimeout(() => {
    if (retryCount < NATIVE_TOKEN_REQUEST.MAX_RETRIES) {
      retryCount += 1;
      isRequestingToken = false;
      lastRequestAt = 0;
      void requestAccessTokenFromNative(lastRequestReason);
      return;
    }

    handleTokenRequestFailure(NATIVE_TOKEN_REQUEST_OUTCOME.TIMEOUT);
  }, NATIVE_TOKEN_REQUEST.TIMEOUT_MS);
};

const sendNativeTokenRequest = (
  reason: NATIVE_TOKEN_REQUEST_REASON
): string => {
  lastRequestReason = reason;
  const platform = detectNativeDesktopPlatform();
  const requestId = uuidv4();
  pendingRequestId = requestId;
  isRequestingToken = true;
  lastRequestAt = Date.now();

  if (platform) {
    postAccessTokenRequestToNative(platform, {
      event: ACCESS_TOKEN_REQUEST_EVENT.REQUEST_ACCESS_TOKEN,
      requestId,
      reason,
      screen: window.location.pathname
    });
  }

  scheduleRequestTimeout();
  dispatchNativeTokenRequestStarted();
  return requestId;
};

export const waitForNativeTokenRefresh = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    pendingResolvers.push({ resolve, reject });
  });
};

export const requestAccessTokenFromNative = async (
  reason: NATIVE_TOKEN_REQUEST_REASON
): Promise<boolean> => {
  if (!isEmbeddedNativeContext()) {
    return false;
  }

  if (
    reason === NATIVE_TOKEN_REQUEST_REASON.MISSING &&
    !hasReceivedNativeTokenPush
  ) {
    return false;
  }

  if (isRequestingToken) {
    try {
      await waitForNativeTokenRefresh();
      return true;
    } catch {
      return false;
    }
  }

  if (isBrowserOffline()) {
    dispatchEmbeddedNetworkUnavailable();
    return false;
  }

  const now = Date.now();
  if (now - lastRequestAt < NATIVE_TOKEN_REQUEST.DEBOUNCE_MS) {
    return false;
  }

  sendNativeTokenRequest(reason);

  try {
    await waitForNativeTokenRefresh();
    return true;
  } catch {
    return false;
  }
};

const clearPendingRefreshState = (token: string): void => {
  isRequestingToken = false;
  retryCount = NATIVE_TOKEN_REQUEST.MIN_RETRIES;
  clearRequestTimeout();
  pendingRequestId = null;
  dispatchNativeTokenRequestEnded();
  resolvePendingResolvers(token);
};

const shouldResolvePendingRefresh = (requestId?: string): boolean => {
  if (!isRequestingToken || !pendingRequestId) {
    return false;
  }

  if (!requestId) {
    return false;
  }

  return requestId === pendingRequestId;
};

const shouldClearPendingRefreshAfterSuccess = (
  options: ApplyNativeAccessTokenOptions
): boolean => {
  if (shouldResolvePendingRefresh(options.requestId)) {
    return true;
  }

  const isNativePush =
    options.source === APPLY_NATIVE_ACCESS_TOKEN_SOURCE.INIT_APP_META_DATA;

  return isRequestingToken && isNativePush;
};

const clearPendingRefreshIfNeeded = (
  accessToken: string,
  options: ApplyNativeAccessTokenOptions
): void => {
  if (shouldClearPendingRefreshAfterSuccess(options)) {
    clearPendingRefreshState(accessToken);
  }
};

const handleRejectedRefreshReply = (): void => {
  clearRequestTimeout();

  if (retryCount < NATIVE_TOKEN_REQUEST.MAX_RETRIES) {
    retryCount += 1;
    isRequestingToken = false;
    lastRequestAt = 0;
    void requestAccessTokenFromNative(lastRequestReason);
    return;
  }

  handleTokenRequestFailure(NATIVE_TOKEN_REQUEST_OUTCOME.MAX_RETRIES);
};

const handleTokenValidationFailure = (
  options: ApplyNativeAccessTokenOptions,
  reason: NATIVE_TOKEN_REQUEST_REASON
): void => {
  if (shouldResolvePendingRefresh(options.requestId)) {
    handleRejectedRefreshReply();
    return;
  }

  if (isRequestingToken) {
    return;
  }

  if (!options.skipNativeRefreshOnFailure) {
    void requestAccessTokenFromNative(reason);
  }
};

export const applyNativeAccessToken = async (
  accessToken: string,
  options: ApplyNativeAccessTokenOptions
): Promise<boolean> => {
  markNativeHostTokenPushReceived(options.source);

  const currentToken = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);
  if (currentToken === accessToken && !isTokenExpired(accessToken)) {
    getAppDispatch()?.(updateIsTokenAddedToInterceptor(true));

    clearPendingRefreshIfNeeded(accessToken, options);

    return true;
  }

  try {
    if (isTokenExpired(accessToken)) {
      handleTokenValidationFailure(
        options,
        NATIVE_TOKEN_REQUEST_REASON.EXPIRED
      );

      return false;
    }

    const isValid = await validateAuth0Token(accessToken);
    if (!isValid) {
      handleTokenValidationFailure(
        options,
        NATIVE_TOKEN_REQUEST_REASON.INVALID
      );

      return false;
    }

    dispatchTokenSuccess(accessToken);

    clearPendingRefreshIfNeeded(accessToken, options);

    return true;
  } catch (error) {
    captureException(error);

    handleTokenValidationFailure(options, NATIVE_TOKEN_REQUEST_REASON.INVALID);

    return false;
  }
};

export const handleNativeTokenRefreshResponse = async (
  accessToken: string,
  requestId?: string
): Promise<void> => {
  await applyNativeAccessToken(accessToken, {
    source: APPLY_NATIVE_ACCESS_TOKEN_SOURCE.SYNC_WITH_DEVICES,
    requestId,
    skipNativeRefreshOnFailure: false
  });
};

export const resetNativeAuthStateForTests = (): void => {
  isRequestingToken = false;
  pendingRequestId = null;
  retryCount = NATIVE_TOKEN_REQUEST.MIN_RETRIES;
  lastRequestAt = 0;
  lastRequestReason = NATIVE_TOKEN_REQUEST_REASON.UNAUTHORIZED;
  hasReceivedNativeTokenPush = false;
  clearRequestTimeout();
  rejectPendingResolvers(new Error('Native auth state reset for tests'));
};
